// System Roles & Permissions Configuration for Pinaki Solutions CRM

export const SYSTEM_ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  USER: "user",
};

export const SYSTEM_ROLE_LABELS = {
  super_admin: "SUPER ADMIN",
  admin: "ADMIN",
  user: "USER",
};

export const SYSTEM_ROLE_DESCRIPTIONS = {
  super_admin: "Full unrestricted access across all workspaces, users, security, settings, and workflows.",
  admin: "Managerial operational access across all modules, team oversight, and in-house stage approvals.",
  user: "Operational team access. Daily tasks, queue views, and execution are driven by assigned Department.",
};

export const DEPARTMENTS = [
  "Sales",
  "Design",
  "Production",
  "Delivery",
  "Accounts",
  "Procurement",
  "Management",
];

export function isSuperAdmin(user) {
  if (!user) return false;
  const role = (user.role || "").toLowerCase().trim();
  return role === "super_admin";
}

export function isAdmin(user) {
  if (!user) return false;
  const role = (user.role || "").toLowerCase().trim();
  return role === "super_admin" || role === "admin";
}

export function getUserDepartments(user) {
  if (!user) return [];
  if (Array.isArray(user.departments) && user.departments.length > 0) {
    return user.departments.map((d) => (d || "").toLowerCase().trim()).filter(Boolean);
  }
  if (user.department) {
    return [(user.department || "").toLowerCase().trim()].filter(Boolean);
  }
  return [];
}

export function formatUserDepartments(user) {
  if (!user) return "";
  if (Array.isArray(user.departments) && user.departments.length > 0) {
    return user.departments.filter(Boolean).join(", ");
  }
  return user.department || "";
}

export function getUserCompanies(user) {
  if (!user) return ["Pinaki Solutions"];
  if (Array.isArray(user.companies) && user.companies.length > 0) {
    return user.companies.map((c) => (c || "").trim()).filter(Boolean);
  }
  if (user.company) {
    return [(user.company || "").trim()].filter(Boolean);
  }
  return ["Pinaki Solutions"];
}

export function formatUserCompanies(user) {
  if (!user) return "Pinaki Solutions";
  const comps = getUserCompanies(user);
  return comps.length > 0 ? comps.join(", ") : "Pinaki Solutions";
}

export function canAccessCompany(user, targetCompany) {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (!targetCompany) return true;
  const userComps = getUserCompanies(user).map((c) => c.toLowerCase().trim());
  const tgt = (targetCompany || "").toLowerCase().trim();
  return userComps.includes(tgt);
}

export function canAccessDepartment(user, allowedDepts = []) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  if (!allowedDepts || allowedDepts.length === 0) return true;

  const userDepts = getUserDepartments(user);

  const allowed = (Array.isArray(allowedDepts) ? allowedDepts : [allowedDepts])
    .map((d) => (d || "").toLowerCase().trim());

  // Check if ANY of user's departments match allowed departments
  for (const userDept of userDepts) {
    if (allowed.includes(userDept)) return true;
    
    // Production team handles both Production and Dispatch workflows
    if (userDept === "production" && allowed.includes("dispatch")) {
      return true;
    }
    // Management department has access across workflows
    if (userDept === "management") {
      return true;
    }
  }

  return false;
}

export function isSalesUser(user) {
  if (!user) return false;
  if (isAdmin(user)) return false;
  return getUserDepartments(user).includes("sales");
}

export function isDesignUser(user) {
  if (!user) return false;
  if (isAdmin(user)) return false;
  return getUserDepartments(user).includes("design");
}

export function isProductionUser(user) {
  if (!user) return false;
  if (isAdmin(user)) return false;
  return getUserDepartments(user).includes("production");
}

export function isProcurementUser(user) {
  if (!user) return false;
  if (isAdmin(user)) return false;
  return getUserDepartments(user).includes("procurement");
}

export function isDeliveryUser(user) {
  if (!user) return false;
  if (isAdmin(user)) return false;
  return getUserDepartments(user).includes("delivery");
}

export function formatRole(role) {
  if (!role) return "USER";
  const r = role.toLowerCase().trim();
  return SYSTEM_ROLE_LABELS[r] || "USER";
}

/**
 * Client Contact Number Visibility Permission Guard:
 * Client contact/phone numbers are ONLY visible to:
 * - SUPER ADMIN
 * - ADMIN
 * - Sales department users (any user with Sales in their departments)
 * All other users and departments must NOT be able to see the client's contact number.
 */
export function canViewClientContact(user) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  return getUserDepartments(user).includes("sales");
}

export function canManageProductionResources(user) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  return getUserDepartments(user).includes("production");
}

export const canViewClientPhone = canViewClientContact;

/**
 * Lead Delete Permission Guard:
 * Authorized users who can delete leads (single & bulk):
 * - SUPER ADMIN
 * - ADMIN
 * - Sales department users (assigned Sales department or role)
 * All other non-sales departments/roles are restricted.
 */
export function canDeleteLead(user) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const depts = getUserDepartments(user);
  if (depts.includes("sales")) return true;
  const role = (user.role || "").toLowerCase().trim();
  if (role === "sales") return true;
  return false;
}

/**
 * Task Edit & Delete Permission Guard:
 * - Super Admin & Admin: Can edit and delete any task across all users and departments.
 * - Normal User: Can edit and delete tasks that they created (assigned_by_id === user.user_id or self-assigned).
 * - Assignees who are not the creator cannot edit or delete someone else's task.
 */
export function canEditOrDeleteTask(user, task) {
  if (!user || !task) return false;
  if (isAdmin(user)) return true;
  const callerId = user.user_id;
  if (!callerId) return false;
  if (task.assigned_by_id && task.assigned_by_id === callerId) return true;
  if (task.created_by_id && task.created_by_id === callerId) return true;
  if (task.created_by && task.created_by === callerId) return true;
  if (user.name && (task.assigned_by_name === user.name || task.created_by === user.name)) return true;
  if (task.is_self_assigned && (task.assigned_to_id === callerId || (Array.isArray(task.assigned_to_ids) && task.assigned_to_ids.includes(callerId)))) return true;
  return false;
}

/**
 * Return whether a user is allowed to reassign a task.
 * - Super Admin / Admin: Can reassign any task.
 * - Current Assignee: Can reassign tasks currently assigned to them.
 * - Task Creator: Can reassign tasks created by them.
 */
export function canReassignTask(user, task) {
  if (!user || !task) return false;
  if (isAdmin(user)) return true;
  const callerId = user.user_id;
  if (!callerId) return false;
  if (task.assigned_by_id && task.assigned_by_id === callerId) return true;
  if (task.created_by_id && task.created_by_id === callerId) return true;
  if (task.assigned_to_id && task.assigned_to_id === callerId) return true;
  if (Array.isArray(task.assigned_to_ids) && task.assigned_to_ids.includes(callerId)) return true;
  if (Array.isArray(task.assignees) && task.assignees.some((a) => a.user_id === callerId)) return true;
  return false;
}

/**
 * Navigation module access definition:
 * Determines which sidebar items to show for a user based on all assigned departments.
 */
export function canAccessNavModule(label, user) {
  if (!user) return false;
  if (label === "Settings") {
    return isSuperAdmin(user);
  }
  if (isAdmin(user)) return true;

  const depts = getUserDepartments(user);

  switch (label) {
    case "Dashboard":
      return false; // Only Admin / Super Admin (or management)
    case "Activities":
    case "Tasks & Reports":
      return true; // Common modules accessible to all team members
    case "Reports":
      return depts.includes("management");
    case "Leads":
    case "Clients":
      return depts.includes("sales") || depts.includes("management");
    case "Orders":
      return depts.includes("sales") || depts.includes("accounts") || depts.includes("management");
    case "Design":
      return depts.includes("design") || depts.includes("management");
    case "Approvals":
      return depts.includes("design") || depts.includes("management");
    case "Production":
      return depts.includes("production") || depts.includes("management");
    case "Dispatch":
      return depts.includes("production") || depts.includes("management");
    case "Delivery":
      return depts.includes("delivery") || depts.includes("production") || depts.includes("management");
    case "Accounts":
      return depts.includes("accounts") || depts.includes("management");
    case "Procurement":
      return depts.includes("procurement") || depts.includes("management");
    case "Users & Roles":
    case "Settings":
      return false; // Reserved for Admin / Super Admin
    default:
      return false;
  }
}

/**
 * Return whether a page route is accessible to a user.
 */
export function isPageAllowedForUser(pageName, user) {
  if (!user) return false;
  if (pageName === "Settings") {
    return isSuperAdmin(user);
  }
  if (isAdmin(user)) return true;

  // Common pages
  const COMMON_PAGES = new Set([
    "Activities",
    "Tasks & Reports",
    "Tasks",
    "TaskDetail",
    "Profile",
  ]);
  if (COMMON_PAGES.has(pageName)) return true;

  if (pageName === "Reports") {
    return getUserDepartments(user).includes("management");
  }

  if (pageName === "Dashboard" || pageName === "Users & Roles" || pageName === "UserDetail" || pageName === "Settings") {
    return false;
  }

  const depts = getUserDepartments(user);

  for (const dept of depts) {
    if (dept === "sales" || dept === "management") {
      if ([
        "Leads", "LeadDetail",
        "Clients", "ClientDetail",
        "Orders", "ClientOrders", "OrderDetail",
        "Procurement", "ProcurementDetail",
        "Accounts", "ClientAccounts"
      ].includes(pageName)) return true;
    }
    if (dept === "design" || dept === "management") {
      if ([
        "Design", "DesignDetail",
        "Approvals",
        "OrderDetail"
      ].includes(pageName)) return true;
    }
    if (dept === "production" || dept === "management") {
      if ([
        "Production", "ProductionDetail",
        "Dispatch", "DispatchDetail",
        "Delivery", "DeliveryPersonDetail", "DeliveryDetail",
        "OrderDetail"
      ].includes(pageName)) return true;
    }
    if (dept === "delivery") {
      if ([
        "Delivery", "DeliveryPersonDetail", "DeliveryDetail",
        "OrderDetail"
      ].includes(pageName)) return true;
    }
    if (dept === "procurement") {
      if ([
        "Procurement", "ProcurementDetail",
        "LeadDetail", "ClientDetail",
        "OrderDetail"
      ].includes(pageName)) return true;
    }
    if (dept === "accounts") {
      if ([
        "Orders", "ClientOrders", "OrderDetail",
        "Invoices", "Payments", "Accounts", "ClientAccounts"
      ].includes(pageName)) return true;
    }
  }

  return false;
}

export function isAccountsAuthorized(user) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const depts = getUserDepartments(user);
  return depts.includes("accounts") || depts.includes("management");
}

export function getDefaultPageForUser(user) {
  if (!user) return "Dashboard";
  if (isAdmin(user)) return "Dashboard";
  return "Activities";
}
